import React, { useEffect, useState, useRef } from 'react';
import { HomeIcon, ActivityIcon, CalendarIcon, PillIcon, BookOpenIcon, TrophyIcon, TimerIcon, SmileIcon, FlaskConicalIcon, UsersIcon, MenuIcon, XIcon } from 'lucide-react';
interface LayoutProps {
  children: React.ReactNode;
  activeScreen: string;
  onNavigate: (screen: string) => void;
}
export const Layout: React.FC<LayoutProps> = ({
  children,
  activeScreen,
  onNavigate
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navItems = [{
    id: 'dashboard',
    icon: HomeIcon,
    label: 'Dashboard'
  }, {
    id: 'bioage',
    icon: ActivityIcon,
    label: 'BioAge'
  }, {
    id: 'protocols',
    icon: CalendarIcon,
    label: 'Protokolle'
  }, {
    id: 'supplements',
    icon: PillIcon,
    label: 'Supplements'
  }, {
    id: 'wissen',
    icon: BookOpenIcon,
    label: 'Wissen'
  }, {
    id: 'challenges',
    icon: TrophyIcon,
    label: 'Challenges'
  }, {
    id: 'fasting',
    icon: TimerIcon,
    label: 'Fasten'
  }, {
    id: 'mood',
    icon: SmileIcon,
    label: 'Stimmung'
  }, {
    id: 'labs',
    icon: FlaskConicalIcon,
    label: 'Smart Labs'
  }, {
    id: 'community',
    icon: UsersIcon,
    label: 'Community'
  }];
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Handle menu item click
  const handleNavClick = (screen: string) => {
    onNavigate(screen);
    setMobileMenuOpen(false);
  };
  return <div className="flex flex-col h-screen w-full bg-gray-50" data-id="element-995">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white shadow-sm" data-id="element-996">
        <div className="flex items-center justify-between p-4" data-id="element-997">
          <div className="flex items-center" data-id="element-998">
            <span className="text-purple-600 text-2xl font-bold" data-id="element-999">•AYA</span>
            <span className="hidden lg:inline ml-2 text-gray-600 text-sm" data-id="element-1000">
              Your Ageless Companion
            </span>
          </div>
          <div className="flex items-center space-x-4" data-id="element-1001">
            {/* Burger Menu Button (visible on screens <= 1024px) */}
            <button className="lg:hidden text-gray-600 hover:text-purple-600 focus:outline-none" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-1002">
              {mobileMenuOpen ? <XIcon size={24} data-id="element-1003" /> : <MenuIcon size={24} data-id="element-1004" />}
            </button>
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-1005">
              <span className="text-purple-600 text-sm font-medium" data-id="element-1006">JD</span>
            </div>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:hidden" data-id="element-1007" />}
      {/* Mobile Menu (Slide-out) */}
      <div ref={menuRef} className={`fixed top-[65px] right-0 bottom-0 w-64 bg-white shadow-lg z-10 transform transition-transform duration-300 ease-in-out lg:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-1008">
        <div className="flex flex-col p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-65px)]" data-id="element-1009">
          {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className={`flex items-center p-3 rounded-lg ${activeScreen === item.id ? 'bg-purple-100 text-purple-600' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1010">
              <item.icon size={20} className="mr-3" data-id="element-1011" />
              <span data-id="element-1012">{item.label}</span>
            </button>)}
        </div>
      </div>
      {/* Main Content */}
      <main className="flex-1 overflow-y-auto" data-id="element-1013">{children}</main>
      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden bg-white border-t border-gray-200 fixed bottom-0 w-full" data-id="element-1014">
        <div className="flex justify-around" data-id="element-1015">
          {navItems.slice(0, 5).map(item => <button key={item.id} onClick={() => onNavigate(item.id)} className={`flex flex-col items-center py-2 px-3 ${activeScreen === item.id ? 'text-purple-600' : 'text-gray-500'}`} data-id="element-1016">
              <item.icon size={20} data-id="element-1017" />
              <span className="text-xs mt-1" data-id="element-1018">{item.label}</span>
            </button>)}
        </div>
      </nav>
      {/* Desktop Sidebar */}
      <nav className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-white shadow-md pt-16" data-id="element-1019">
        <div className="flex flex-col p-4 space-y-2" data-id="element-1020">
          {navItems.map(item => <button key={item.id} onClick={() => onNavigate(item.id)} className={`flex items-center p-3 rounded-lg ${activeScreen === item.id ? 'bg-purple-100 text-purple-600' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1021">
              <item.icon size={20} className="mr-3" data-id="element-1022" />
              <span data-id="element-1023">{item.label}</span>
            </button>)}
        </div>
      </nav>
    </div>;
};